/**
 * Influence map — Layer 3's space analysis. (§10.1)
 *
 * The brief asks for "Voronoi/influence-map over the pitch grid (32×20 cells)".
 * We build the influence map rather than a true Voronoi decomposition: a Voronoi
 * cell answers "who is nearest", which is a strictly worse question than "who
 * controls this space". Two players 1m apart own their region jointly; a player
 * sprinting away from a cell does not own it at all. Influence answers both for
 * the price of a subtraction, and it is what the rest of Layer 3 actually needs:
 * `control[cell] = ownInfluence − oppInfluence`, positive where we can receive.
 *
 * ── Two decisions worth knowing about ──────────────────────────────────────
 *
 * 1. FALLOFF IS (1 − d/R)², NOT exp(−d/R). The exponential is the textbook
 *    choice and it is wrong here: `Math.exp` is not correctly rounded and is
 *    engine-dependent (DECISIONS.md D2), and there is no `dexp` wrapper to hide
 *    behind. The quadratic uses only `+ - * /` and `sqrt`, which ARE correctly
 *    rounded everywhere, so this grid is bit-identical on every engine for free
 *    — the same trick `dpow025` plays for the Magnus coefficient. It also has
 *    compact support, which is why the rebuild below can scan a bounding box
 *    instead of all 640 cells per player.
 *
 * 2. INFLUENCE IS PROJECTED ALONG VELOCITY. A player at a standstill owns the
 *    space around him; a player at 8 m/s owns the space he is arriving at. Half
 *    a second of lead (AI_INFLUENCE_VELOCITY_LEAD_S) is what makes a covering
 *    run register as cover BEFORE it arrives, which is the whole reason a
 *    defender reads a through ball as risky.
 *
 * NO STATS ENTER THIS MAP (§10.2). A player's influence is a function of where
 * he is and where he is going, never of how good he is. That keeps the space
 * model honest and keeps difficulty out of it.
 *
 * Allocation: everything is sized at construction. rebuild() fills in place.
 */

import type { WorldState } from '../sim/types.js';
import { MAX_PLAYERS } from '../sim/types.js';
import {
  AI_INFLUENCE_GRID_X,
  AI_INFLUENCE_GRID_Z,
  AI_INFLUENCE_PLAYER_RADIUS,
  AI_INFLUENCE_VELOCITY_LEAD_S,
  AI_SPACE_DISTANCE_PENALTY,
  AI_SPACE_FORWARD_BIAS,
} from '../sim/constants.js';

const CELL_COUNT = AI_INFLUENCE_GRID_X * AI_INFLUENCE_GRID_Z;

/** A world-space point written by bestCellNear(). Caller owns it; we only fill. */
export interface SpacePoint {
  x: number;
  z: number;
  /** control value at that cell — how strongly we own it. */
  control: number;
}

export function createSpacePoint(): SpacePoint {
  return { x: 0, z: 0, control: 0 };
}

export class InfluenceMap {
  /** Influence of the team this map belongs to. */
  readonly own = new Float64Array(CELL_COUNT);
  /** Influence of the opposition. */
  readonly opp = new Float64Array(CELL_COUNT);
  /** own − opp. Positive ⇒ we control this cell. */
  readonly control = new Float64Array(CELL_COUNT);

  /** Pitch geometry, refreshed on every rebuild — pitch size is data (§9.2). */
  private halfLength = 0;
  private halfWidth = 0;
  private cellW = 1;
  private cellD = 1;

  /**
   * Rebuild for `team`'s point of view. Called at AI_TEAM_TICK_RATE (5Hz), never
   * per tick — the map being up to 200ms stale is not a bug, it is §10.2's
   * imperfect information showing up for free.
   */
  rebuild(state: WorldState, team: number): void {
    this.halfLength = state.config.pitchLength * 0.5;
    this.halfWidth = state.config.pitchWidth * 0.5;
    this.cellW = state.config.pitchLength / AI_INFLUENCE_GRID_X;
    this.cellD = state.config.pitchWidth / AI_INFLUENCE_GRID_Z;

    this.own.fill(0);
    this.opp.fill(0);

    const p = state.players;
    const r = AI_INFLUENCE_PLAYER_RADIUS;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;

      // Lead the player along his own velocity — see note 2 in the header.
      const lx = p.px[i]! + p.vx[i]! * AI_INFLUENCE_VELOCITY_LEAD_S;
      const lz = p.pz[i]! + p.vz[i]! * AI_INFLUENCE_VELOCITY_LEAD_S;
      const grid = p.team[i] === team ? this.own : this.opp;

      // Compact support ⇒ only the cells inside the radius can change.
      const ix0 = this.clampIx(this.cellXOf(lx - r));
      const ix1 = this.clampIx(this.cellXOf(lx + r));
      const iz0 = this.clampIz(this.cellZOf(lz - r));
      const iz1 = this.clampIz(this.cellZOf(lz + r));

      for (let ix = ix0; ix <= ix1; ix++) {
        const cx = this.cellCentreX(ix);
        const dx = cx - lx;
        for (let iz = iz0; iz <= iz1; iz++) {
          const cz = this.cellCentreZ(iz);
          const dz = cz - lz;
          const d = Math.sqrt(dx * dx + dz * dz);
          if (d >= r) continue;
          const w = 1 - d / r;
          grid[ix * AI_INFLUENCE_GRID_Z + iz]! += w * w;
        }
      }
    }

    for (let c = 0; c < CELL_COUNT; c++) {
      this.control[c] = this.own[c]! - this.opp[c]!;
    }
  }

  /** How strongly we control the cell containing (x, z). */
  controlAt(x: number, z: number): number {
    const ix = this.clampIx(this.cellXOf(x));
    const iz = this.clampIz(this.cellZOf(z));
    return this.control[ix * AI_INFLUENCE_GRID_Z + iz]!;
  }

  /** How strongly the OPPOSITION covers (x, z). The pressure a receiver inherits. */
  opponentInfluenceAt(x: number, z: number): number {
    const ix = this.clampIx(this.cellXOf(x));
    const iz = this.clampIz(this.cellZOf(z));
    return this.opp[ix * AI_INFLUENCE_GRID_Z + iz]!;
  }

  /**
   * Best cell to move into within `radius` of (cx, cz), written to `out`.
   *
   * Score = control + forward bias − distance penalty. All three terms matter:
   * control alone sends runners into the empty space behind their own keeper;
   * control + forward alone sends every forward to the same corner flag; the
   * distance penalty is what keeps the run believable and the shape intact.
   *
   * Falls back to (cx, cz) if the radius covers no cell — never returns garbage.
   */
  bestCellNear(
    cx: number,
    cz: number,
    radius: number,
    attackSign: number,
    out: SpacePoint,
    // Hysteresis: the caller's PREVIOUS chosen point + a score bonus for the
    // cell containing it. A Support runner's arg-max otherwise hops between
    // adjacent ~3.3m cells every 5Hz as the control field wobbles, which reads
    // as aimless roaming. With a keep-bonus the old cell holds unless a new one
    // is clearly better. Default NaN/0 → unchanged behaviour for other callers.
    keepX = Number.NaN,
    keepZ = Number.NaN,
    keepBonus = 0,
  ): void {
    out.x = cx;
    out.z = cz;
    out.control = this.controlAt(cx, cz);

    const ix0 = this.clampIx(this.cellXOf(cx - radius));
    const ix1 = this.clampIx(this.cellXOf(cx + radius));
    const iz0 = this.clampIz(this.cellZOf(cz - radius));
    const iz1 = this.clampIz(this.cellZOf(cz + radius));

    const hasKeep = keepBonus > 0 && Number.isFinite(keepX) && Number.isFinite(keepZ);
    const keepIx = hasKeep ? this.clampIx(this.cellXOf(keepX)) : -1;
    const keepIz = hasKeep ? this.clampIz(this.cellZOf(keepZ)) : -1;

    let best = Number.NEGATIVE_INFINITY;
    for (let ix = ix0; ix <= ix1; ix++) {
      const x = this.cellCentreX(ix);
      const dx = x - cx;
      // Normalized so the bias is a weight, not a length in metres.
      const forward = (attackSign * x) / (this.halfLength > 0 ? this.halfLength : 1);
      for (let iz = iz0; iz <= iz1; iz++) {
        const z = this.cellCentreZ(iz);
        const dz = z - cz;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d > radius) continue;
        let score =
          this.control[ix * AI_INFLUENCE_GRID_Z + iz]! +
          forward * AI_SPACE_FORWARD_BIAS -
          (d / radius) * AI_SPACE_DISTANCE_PENALTY;
        if (ix === keepIx && iz === keepIz) score += keepBonus;
        if (score > best) {
          best = score;
          out.x = x;
          out.z = z;
          out.control = this.control[ix * AI_INFLUENCE_GRID_Z + iz]!;
        }
      }
    }
  }

  // -- grid <-> world -------------------------------------------------------

  private cellXOf(x: number): number {
    return Math.floor((x + this.halfLength) / this.cellW);
  }

  private cellZOf(z: number): number {
    return Math.floor((z + this.halfWidth) / this.cellD);
  }

  private cellCentreX(ix: number): number {
    return -this.halfLength + (ix + 0.5) * this.cellW;
  }

  private cellCentreZ(iz: number): number {
    return -this.halfWidth + (iz + 0.5) * this.cellD;
  }

  private clampIx(ix: number): number {
    return ix < 0 ? 0 : ix >= AI_INFLUENCE_GRID_X ? AI_INFLUENCE_GRID_X - 1 : ix;
  }

  private clampIz(iz: number): number {
    return iz < 0 ? 0 : iz >= AI_INFLUENCE_GRID_Z ? AI_INFLUENCE_GRID_Z - 1 : iz;
  }
}
